import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { ShiftDetail, UpdateShiftInput } from '../types/shift';

type EditableShift = Pick<ShiftDetail, 'title' | 'code' | 'startTime' | 'endTime' | 'color'>;

interface EditShiftModalProps {
  open: boolean;
  shift: EditableShift | null;
  onClose: () => void;
  onSubmit: (data: UpdateShiftInput) => Promise<void>;
}

export function EditShiftModal({ open, shift, onClose, onSubmit }: EditShiftModalProps) {
  const [form, setForm] = useState<UpdateShiftInput>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Re-seed the form from the shift each time the modal opens.
  useEffect(() => {
    if (open) {
      setForm({
        title: shift?.title ?? '',
        code: shift?.code ?? undefined,
        startTime: shift?.startTime ?? '',
        endTime: shift?.endTime ?? '',
        color: shift?.color ?? undefined,
      });
    }
  }, [open, shift]);

  // <input type="time"> produces zero-padded "HH:mm", so a string comparison
  // works as a chronological one. Equal times are also rejected (WRK-5450).
  const hasTimeError = !!form.startTime && !!form.endTime && form.endTime <= form.startTime;

  const handleSubmit = async () => {
    if (!form.title?.trim() || !form.code?.trim() || !form.startTime || !form.endTime) return;
    if (hasTimeError) return;
    setIsSubmitting(true);
    try {
      await onSubmit(form);
      onClose();
      showSuccessToast('Shift Updated', 'Shift details updated successfully.');
    } catch (error: unknown) {
      showErrorToast(
        'Failed to Update Shift',
        error instanceof Error ? error.message : 'An unexpected error occurred.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Shift</DialogTitle>
          <DialogDescription>Update shift details</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">
              Shift Title <span className="text-red-500">*</span>
            </label>
            <Input
              value={form.title ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Enter shift title"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">
              Shift Code <span className="text-red-500">*</span>
            </label>
            <Input
              value={form.code ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value || undefined }))}
              placeholder="Enter shift code"
            />
          </div>

          <div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Start Time <span className="text-red-500">*</span>
                </label>
                <Input
                  type="time"
                  value={form.startTime ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
                  aria-invalid={hasTimeError}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  End Time <span className="text-red-500">*</span>
                </label>
                <Input
                  type="time"
                  value={form.endTime ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
                  aria-invalid={hasTimeError}
                />
              </div>
            </div>
            {hasTimeError && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                End time must be after start time.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="bg-orange-600 hover:bg-orange-700 text-white"
            onClick={handleSubmit}
            disabled={
              !form.title?.trim() ||
              !form.code?.trim() ||
              !form.startTime ||
              !form.endTime ||
              hasTimeError ||
              isSubmitting
            }
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
